import { z } from "zod";
import type { EstadoCita, MetodoPago } from "../estados";
import { crearClienteSchema } from "./clientes";
import { instante, motivo, uuid } from "./comunes";

export const MENSAJE_SIN_SERVICIO = "Seleccione un servicio para esta cita";

/** Servicios de la cita, en orden: el primero es el principal. Un servicio puede repetirse. */
const servicioIds = z
  .array(uuid, { message: MENSAJE_SIN_SERVICIO })
  .min(1, MENSAJE_SIN_SERVICIO)
  .max(10);

/** Compatibilidad: la cola offline y clientes viejos mandan solo `servicioId`. */
function conServicioIds(v: unknown): unknown {
  if (
    v &&
    typeof v === "object" &&
    !("servicioIds" in v) &&
    "servicioId" in v
  ) {
    const { servicioId, ...resto } = v as Record<string, unknown>;
    return { ...resto, servicioIds: [servicioId] };
  }
  return v;
}

export const crearCitaSchema = z.preprocess(
  conServicioIds,
  z
    .object({
      clienteId: uuid.optional(),
      clienteNuevo: crearClienteSchema.optional(),
      servicioIds,
      barberoId: uuid.nullable().optional(),
      barberoPreferidoId: uuid.nullable().optional(),
      inicio: instante,
      origen: z.enum(["web", "app", "walk_in"]).default("web"),
      notas: z.string().trim().max(500).optional(),
    })
    .refine((v) => !!v.clienteId !== !!v.clienteNuevo, {
      message: "Indica un cliente existente o los datos de uno nuevo",
      path: ["clienteId"],
    }),
);
export type CrearCitaInput = z.infer<typeof crearCitaSchema>;

/** Edición completa de una cita aún no iniciada (cliente, servicio, barbero, fecha, hora y notas). */
export const editarCitaSchema = z.preprocess(
  conServicioIds,
  z
    .object({
      clienteId: uuid.optional(),
      clienteNuevo: crearClienteSchema.optional(),
      servicioIds,
      barberoId: uuid.nullable().optional(),
      barberoPreferidoId: uuid.nullable().optional(),
      inicio: instante,
      notas: z.string().trim().max(500).nullable().optional(),
    })
    .refine((v) => !!v.clienteId !== !!v.clienteNuevo, {
      message: "Indica un cliente existente o los datos de uno nuevo",
      path: ["clienteId"],
    }),
);
export type EditarCitaInput = z.infer<typeof editarCitaSchema>;

export const reprogramarCitaSchema = z.object({
  inicio: instante,
  barberoId: uuid.nullable().optional(),
  /** Si viene, también cambia el barbero preferido (al arrastrar a un barbero). Si no, se conserva. */
  barberoPreferidoId: uuid.nullable().optional(),
  motivo: z.string().trim().max(300).optional(),
});
export type ReprogramarCitaInput = z.infer<typeof reprogramarCitaSchema>;

export const asignarBarberoSchema = z.object({ barberoId: uuid });
export const cancelarCitaSchema = z.object({ motivo });
export const adelantarCitaSchema = z.object({
  inicio: instante,
  confirmoCliente: z.literal(true, {
    message: "Confirma que el cliente aceptó adelantar su cita",
  }),
});

export interface CitaVista {
  id: string;
  cliente: {
    id: string;
    nombre: string;
    telefono: string;
    citasTotal: number;
    cancelaciones: number;
    retardos: number;
    noShows: number;
  };
  /** Primer servicio (se conserva para la app móvil instalada). */
  servicio: { id: string; nombre: string; precio: number };
  /** Todos los servicios de la cita, en orden; el primero es `servicio`. */
  servicios: { id: string; nombre: string; precio: number }[];
  /** Métodos de pago de la venta pagada; solo con `ventas.ver_detalle`. */
  metodosPago?: MetodoPago[];
  barberoId: string | null;
  barbero: string | null;
  barberoPreferidoId: string | null;
  inicio: string;
  fin: string;
  estado: EstadoCita;
  origen: string;
  llegoTarde: boolean;
  toleranciaHasta: string | null;
  motivoCancelacion: string | null;
  notas: string | null;
  /** `ventaId`: carrito de la cita (para ver su detalle). */
  atencion: { id: string; inicio: string; fin: string | null; ventaId: string | null } | null;
}

export interface AgendaDia {
  dia: string;
  barberos: {
    id: string;
    nombre: string;
    horario: {
      entrada: string;
      salida: string;
      comidaInicio: string | null;
      comidaFin: string | null;
      esDescanso: boolean;
    } | null;
  }[];
  citas: CitaVista[];
}

export interface SlotDisponible {
  inicio: string;
  fin: string;
}

/**
 * Detalles del 409 cuando la hora choca con otra cita del barbero: el hueco libre más cercano antes y
 * después (mismo día) y, por compatibilidad con la app ya instalada, hasta 3 libres desde la hora pedida.
 */
export interface DetallesChoque {
  antes: SlotDisponible | null;
  despues: SlotDisponible | null;
  alternativas: SlotDisponible[];
}

/** Horario del día con su estado (`EstadoHorario`): el selector de hora muestra también los ocupados. */
export interface HorarioDelDia extends SlotDisponible {
  estado: 'libre' | 'ocupado' | 'no_cabe';
}
